/**
 * @module stores/uiStore
 * @description UI state management
 *
 * Manages all UI-related state including:
 * - Modal visibility and state
 * - Context menu state
 * - Panel sizes and preferences
 * - Loading states
 * - Resize handling
 *
 * @example
 * import { uiStore, showAuthModal, hideAuthModal } from '@stores/uiStore';
 *
 * // Show auth modal
 * showAuthModal('api-key');
 *
 * // Show context menu
 * showItemContextMenu(event, item);
 *
 * // Update panel width
 * setCollectionsPanelWidth(300);
 */

import { writable, derived, get } from 'svelte/store';
import { ui } from '@/config';
import { createLogger } from '@services/loggerService';
import { indexedDBService } from '@services/indexedDBService';
import type {
  AuthModalState,
  CollectionModalState,
  ItemContextMenuState,
  CollectionContextMenuState,
  UIPanelPreferences,
  LoadingState,
  ErrorState,
  DetailsPanelMode,
  ResizeState,
  Collection,
  Item,
  ItemType,
} from '@types';

const log = createLogger('ui');

// =============================================================================
// TYPES
// =============================================================================

/** Export modal state */
export interface ExportModalState {
  /** Whether modal is visible */
  visible: boolean;
  /** Items to export */
  items: Item[];
}

/** New item modal state */
export interface NewItemModalState {
  /** Whether modal is visible */
  visible: boolean;
  /** Selected item type */
  selectedItemType: ItemType;
}

/** Settings modal state */
export interface SettingsModalState {
  /** Whether modal is visible */
  visible: boolean;
}

/** Complete UI state */
export interface UIState {
  /** Auth modal state */
  authModal: AuthModalState;
  /** Collection modal state */
  collectionModal: CollectionModalState;
  /** Export modal state */
  exportModal: ExportModalState;
  /** New item modal state */
  newItemModal: NewItemModalState;
  /** Settings modal state */
  settingsModal: SettingsModalState;
  /** Item context menu state */
  itemContextMenu: ItemContextMenuState;
  /** Collection context menu state */
  collectionContextMenu: CollectionContextMenuState;
  /** Panel preferences */
  panels: UIPanelPreferences;
  /** Loading states */
  loading: LoadingState;
  /** Error state */
  error: ErrorState;
  /** Details panel mode */
  detailsMode: DetailsPanelMode;
  /** Resize state */
  resize: ResizeState;
  /** Whether sidebar is collapsed */
  sidebarCollapsed: boolean;
  /** Whether details panel is visible */
  detailsPanelVisible: boolean;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: UIState = {
  authModal: {
    visible: false,
    step: 'api-key',
  },
  collectionModal: {
    visible: false,
    mode: 'create',
    collectionName: '',
    parentCollection: null,
  },
  exportModal: {
    visible: false,
    items: [],
  },
  newItemModal: {
    visible: false,
    selectedItemType: 'book',
  },
  settingsModal: {
    visible: false,
  },
  itemContextMenu: {
    visible: false,
    x: 0,
    y: 0,
    item: null,
  },
  collectionContextMenu: {
    visible: false,
    x: 0,
    y: 0,
    collection: null,
    isSpecial: false,
  },
  panels: {
    collectionsWidth: ui.defaultCollectionsWidth,
    detailsWidth: ui.defaultDetailsWidth,
  },
  loading: {
    global: false,
    items: false,
    collections: false,
    item: false,
  },
  error: {
    message: null,
    code: undefined,
    context: undefined,
  },
  detailsMode: 'view',
  resize: {
    isResizing: false,
    panel: null,
    startX: 0,
    startWidth: 0,
  },
  sidebarCollapsed: false,
  detailsPanelVisible: true,
};

// =============================================================================
// STORE
// =============================================================================

/** Internal writable store */
const store = writable<UIState>(initialState);

/** Exported UI store (read-only subscribe) */
export const uiStore = {
  subscribe: store.subscribe,
};

// =============================================================================
// DERIVED STORES - MODALS
// =============================================================================

/** Auth modal state */
export const authModal = derived(store, ($store) => $store.authModal);

/** Whether auth modal is visible */
export const isAuthModalVisible = derived(store, ($store) => $store.authModal.visible);

/** Collection modal state */
export const collectionModal = derived(store, ($store) => $store.collectionModal);

/** Whether collection modal is visible */
export const isCollectionModalVisible = derived(store, ($store) => $store.collectionModal.visible);

/** Export modal state */
export const exportModal = derived(store, ($store) => $store.exportModal);

/** Whether export modal is visible */
export const isExportModalVisible = derived(store, ($store) => $store.exportModal.visible);

/** New item modal state */
export const newItemModal = derived(store, ($store) => $store.newItemModal);

/** Whether new item modal is visible */
export const isNewItemModalVisible = derived(store, ($store) => $store.newItemModal.visible);

/** Settings modal state */
export const settingsModal = derived(store, ($store) => $store.settingsModal);

/** Whether settings modal is visible */
export const isSettingsModalVisible = derived(store, ($store) => $store.settingsModal.visible);

// =============================================================================
// DERIVED STORES - CONTEXT MENUS
// =============================================================================

/** Item context menu state */
export const itemContextMenu = derived(store, ($store) => $store.itemContextMenu);

/** Whether item context menu is visible */
export const isItemContextMenuVisible = derived(store, ($store) => $store.itemContextMenu.visible);

/** Collection context menu state */
export const collectionContextMenu = derived(store, ($store) => $store.collectionContextMenu);

/** Whether collection context menu is visible */
export const isCollectionContextMenuVisible = derived(
  store,
  ($store) => $store.collectionContextMenu.visible
);

// =============================================================================
// DERIVED STORES - PANELS
// =============================================================================

/** Panel preferences */
export const panelPreferences = derived(store, ($store) => $store.panels);

/** Collections panel width */
export const collectionsPanelWidth = derived(store, ($store) => $store.panels.collectionsWidth);

/** Details panel width */
export const detailsPanelWidth = derived(store, ($store) => $store.panels.detailsWidth);

/** Whether sidebar is collapsed */
export const isSidebarCollapsed = derived(store, ($store) => $store.sidebarCollapsed);

/** Whether details panel is visible */
export const isDetailsPanelVisible = derived(store, ($store) => $store.detailsPanelVisible);

/** Details panel mode */
export const detailsMode = derived(store, ($store) => $store.detailsMode);

// =============================================================================
// DERIVED STORES - LOADING & ERROR
// =============================================================================

/** Loading states */
export const loadingState = derived(store, ($store) => $store.loading);

/** Global loading state */
export const isGlobalLoading = derived(store, ($store) => $store.loading.global);

/** Any loading active */
export const isAnyLoading = derived(
  store,
  ($store) =>
    $store.loading.global ||
    $store.loading.items ||
    $store.loading.collections ||
    $store.loading.item
);

/** Error state */
export const errorState = derived(store, ($store) => $store.error);

/** Whether there's an error */
export const hasError = derived(store, ($store) => $store.error.message !== null);

// =============================================================================
// DERIVED STORES - RESIZE
// =============================================================================

/** Resize state */
export const resizeState = derived(store, ($store) => $store.resize);

/** Whether currently resizing */
export const isResizing = derived(store, ($store) => $store.resize.isResizing);

// =============================================================================
// ACTIONS - AUTH MODAL
// =============================================================================

/**
 * Show auth modal
 */
export function showAuthModal(step: 'api-key' | 'library-select' = 'api-key'): void {
  log.debug('Showing auth modal', { step });
  store.update((s) => ({
    ...s,
    authModal: { visible: true, step },
  }));
}

/**
 * Hide auth modal
 */
export function hideAuthModal(): void {
  log.debug('Hiding auth modal');
  store.update((s) => ({
    ...s,
    authModal: { visible: false, step: 'api-key' },
  }));
}

/**
 * Set auth modal step
 */
export function setAuthModalStep(step: 'api-key' | 'library-select'): void {
  store.update((s) => ({
    ...s,
    authModal: { ...s.authModal, step },
  }));
}

// =============================================================================
// ACTIONS - COLLECTION MODAL
// =============================================================================

/**
 * Show collection create modal
 */
export function showCreateCollectionModal(parentCollection?: Collection): void {
  log.debug('Showing create collection modal');
  store.update((s) => ({
    ...s,
    collectionModal: {
      visible: true,
      mode: parentCollection ? 'create-sub' : 'create',
      collectionName: '',
      parentCollection: parentCollection || null,
    },
  }));
}

/**
 * Show collection rename modal
 */
export function showRenameCollectionModal(collection: Collection): void {
  log.debug('Showing rename collection modal', { key: collection.key });
  store.update((s) => ({
    ...s,
    collectionModal: {
      visible: true,
      mode: 'rename',
      collectionName: collection.data.name,
      parentCollection: null,
      editingCollection: collection,
    },
  }));
}

/**
 * Hide collection modal
 */
export function hideCollectionModal(): void {
  log.debug('Hiding collection modal');
  store.update((s) => ({
    ...s,
    collectionModal: {
      visible: false,
      mode: 'create',
      collectionName: '',
      parentCollection: null,
      editingCollection: undefined,
    },
  }));
}

/**
 * Update collection modal name
 */
export function setCollectionModalName(name: string): void {
  store.update((s) => ({
    ...s,
    collectionModal: { ...s.collectionModal, collectionName: name },
  }));
}

// =============================================================================
// ACTIONS - EXPORT MODAL
// =============================================================================

/**
 * Show export modal with items
 */
export function showExportModal(items: Item[]): void {
  log.debug('Showing export modal', { itemCount: items.length });
  store.update((s) => ({
    ...s,
    exportModal: {
      visible: true,
      items,
    },
  }));
}

/**
 * Hide export modal
 */
export function hideExportModal(): void {
  log.debug('Hiding export modal');
  store.update((s) => ({
    ...s,
    exportModal: {
      visible: false,
      items: [],
    },
  }));
}

// =============================================================================
// ACTIONS - NEW ITEM MODAL
// =============================================================================

/**
 * Show new item modal
 */
export function showNewItemModal(): void {
  log.debug('Showing new item modal');
  store.update((s) => ({
    ...s,
    newItemModal: {
      visible: true,
      selectedItemType: 'book',
    },
  }));
}

/**
 * Hide new item modal
 */
export function hideNewItemModal(): void {
  log.debug('Hiding new item modal');
  store.update((s) => ({
    ...s,
    newItemModal: {
      visible: false,
      selectedItemType: 'book',
    },
  }));
}

/**
 * Set selected item type in new item modal
 */
export function setNewItemType(itemType: ItemType): void {
  store.update((s) => ({
    ...s,
    newItemModal: {
      ...s.newItemModal,
      selectedItemType: itemType,
    },
  }));
}

// =============================================================================
// ACTIONS - SETTINGS MODAL
// =============================================================================

/**
 * Show settings modal
 */
export function showSettingsModal(): void {
  log.debug('Showing settings modal');
  store.update((s) => ({
    ...s,
    settingsModal: { visible: true },
  }));
}

/**
 * Hide settings modal
 */
export function hideSettingsModal(): void {
  log.debug('Hiding settings modal');
  store.update((s) => ({
    ...s,
    settingsModal: { visible: false },
  }));
}

// =============================================================================
// ACTIONS - ITEM CONTEXT MENU
// =============================================================================

/**
 * Show item context menu
 */
export function showItemContextMenu(
  event: MouseEvent | { clientX: number; clientY: number },
  item: Item
): void {
  // Only call preventDefault if it's a real MouseEvent
  if ('preventDefault' in event && typeof event.preventDefault === 'function') {
    event.preventDefault();
  }
  log.debug('Showing item context menu', { key: item.key });
  store.update((s) => ({
    ...s,
    itemContextMenu: {
      visible: true,
      x: event.clientX,
      y: event.clientY,
      item,
    },
  }));
}

/**
 * Hide item context menu
 */
export function hideItemContextMenu(): void {
  store.update((s) => ({
    ...s,
    itemContextMenu: {
      visible: false,
      x: 0,
      y: 0,
      item: null,
    },
  }));
}

// =============================================================================
// ACTIONS - COLLECTION CONTEXT MENU
// =============================================================================

/**
 * Show collection context menu
 */
export function showCollectionContextMenu(
  event: MouseEvent | { clientX: number; clientY: number },
  collection: Collection | null,
  isSpecial: boolean = false
): void {
  // Only call preventDefault if it's a real MouseEvent
  if ('preventDefault' in event && typeof event.preventDefault === 'function') {
    event.preventDefault();
  }
  log.debug('Showing collection context menu', {
    key: collection?.key,
    isSpecial,
  });
  store.update((s) => ({
    ...s,
    collectionContextMenu: {
      visible: true,
      x: event.clientX,
      y: event.clientY,
      collection,
      isSpecial,
    },
  }));
}

/**
 * Hide collection context menu
 */
export function hideCollectionContextMenu(): void {
  store.update((s) => ({
    ...s,
    collectionContextMenu: {
      visible: false,
      x: 0,
      y: 0,
      collection: null,
      isSpecial: false,
    },
  }));
}

/**
 * Hide all context menus
 */
export function hideAllContextMenus(): void {
  store.update((s) => ({
    ...s,
    itemContextMenu: { visible: false, x: 0, y: 0, item: null },
    collectionContextMenu: {
      visible: false,
      x: 0,
      y: 0,
      collection: null,
      isSpecial: false,
    },
  }));
}

// =============================================================================
// ACTIONS - PANELS
// =============================================================================

/**
 * Set collections panel width
 */
export function setCollectionsPanelWidth(width: number): void {
  const clampedWidth = Math.max(150, Math.min(500, width));
  store.update((s) => ({
    ...s,
    panels: { ...s.panels, collectionsWidth: clampedWidth },
  }));
  indexedDBService.setUIPreferences({ collectionsWidth: clampedWidth });
}

/**
 * Set details panel width
 */
export function setDetailsPanelWidth(width: number): void {
  const clampedWidth = Math.max(250, Math.min(600, width));
  store.update((s) => ({
    ...s,
    panels: { ...s.panels, detailsWidth: clampedWidth },
  }));
  indexedDBService.setUIPreferences({ detailsWidth: clampedWidth });
}

/**
 * Toggle sidebar collapsed state
 */
export function toggleSidebar(): void {
  store.update((s) => ({
    ...s,
    sidebarCollapsed: !s.sidebarCollapsed,
  }));
}

/**
 * Toggle details panel visibility
 */
export function toggleDetailsPanel(): void {
  store.update((s) => ({
    ...s,
    detailsPanelVisible: !s.detailsPanelVisible,
  }));
}

/**
 * Show details panel
 */
export function showDetailsPanel(): void {
  store.update((s) => ({ ...s, detailsPanelVisible: true }));
}

/**
 * Hide details panel
 */
export function hideDetailsPanel(): void {
  store.update((s) => ({ ...s, detailsPanelVisible: false }));
}

/**
 * Set details panel mode
 */
export function setDetailsMode(mode: DetailsPanelMode): void {
  log.debug('Setting details mode', { mode });
  store.update((s) => ({ ...s, detailsMode: mode }));
}

// =============================================================================
// ACTIONS - RESIZE
// =============================================================================

/**
 * Start resizing a panel
 */
export function startResize(panel: 'collections' | 'details', startX: number): void {
  const state = get(store);
  const startWidth =
    panel === 'collections' ? state.panels.collectionsWidth : state.panels.detailsWidth;

  store.update((s) => ({
    ...s,
    resize: {
      isResizing: true,
      panel,
      startX,
      startWidth,
    },
  }));
}

/**
 * Update resize position
 */
export function updateResize(currentX: number): void {
  const state = get(store);
  if (!state.resize.isResizing || !state.resize.panel) return;

  const delta = currentX - state.resize.startX;
  const newWidth =
    state.resize.panel === 'collections'
      ? state.resize.startWidth + delta
      : state.resize.startWidth - delta;

  if (state.resize.panel === 'collections') {
    setCollectionsPanelWidth(newWidth);
  } else {
    setDetailsPanelWidth(newWidth);
  }
}

/**
 * Stop resizing
 */
export function stopResize(): void {
  store.update((s) => ({
    ...s,
    resize: {
      isResizing: false,
      panel: null,
      startX: 0,
      startWidth: 0,
    },
  }));
}

// =============================================================================
// ACTIONS - LOADING
// =============================================================================

/**
 * Set global loading state
 */
export function setGlobalLoading(loading: boolean): void {
  store.update((s) => ({
    ...s,
    loading: { ...s.loading, global: loading },
  }));
}

/**
 * Set items loading state
 */
export function setItemsLoading(loading: boolean): void {
  store.update((s) => ({
    ...s,
    loading: { ...s.loading, items: loading },
  }));
}

/**
 * Set collections loading state
 */
export function setCollectionsLoading(loading: boolean): void {
  store.update((s) => ({
    ...s,
    loading: { ...s.loading, collections: loading },
  }));
}

/**
 * Set single item loading state
 */
export function setItemLoading(loading: boolean): void {
  store.update((s) => ({
    ...s,
    loading: { ...s.loading, item: loading },
  }));
}

// =============================================================================
// ACTIONS - ERROR
// =============================================================================

/**
 * Set error state
 */
export function setError(message: string, code?: string, context?: ErrorState['context']): void {
  log.error('UI error', { message, code, context });
  store.update((s) => ({
    ...s,
    error: { message, code, context },
  }));
}

/**
 * Clear error state
 */
export function clearError(): void {
  store.update((s) => ({
    ...s,
    error: { message: null, code: undefined, context: undefined },
  }));
}

// =============================================================================
// ACTIONS - INITIALIZATION
// =============================================================================

/**
 * Initialize UI from stored preferences
 */
export function initUIStore(): void {
  log.info('Initializing UI store');
  const prefs = indexedDBService.getUIPreferences();

  store.update((s) => ({
    ...s,
    panels: {
      collectionsWidth: prefs.collectionsWidth ?? s.panels.collectionsWidth,
      detailsWidth: prefs.detailsWidth ?? s.panels.detailsWidth,
    },
  }));
}

/**
 * Get current UI state
 */
export function getUIState(): UIState {
  return get(store);
}

// =============================================================================
// STORE RESET
// =============================================================================

/**
 * Reset store to initial state
 */
export function resetUIStore(): void {
  store.set({ ...initialState });
}

export default uiStore;
